import { useMemo, useRef } from "react";
import envConfig from "../../utils/env.config";
import { RESTAURANT_IMAGE_SOURCE, Restaurant } from "../../utils/types";
import {
  hydrateRestaurantFromProps,
  setSocialMedia,
} from "../redux/slices/configSlice";
import { setHasRoundingMethodAndLevel } from "../redux/slices/cartAndLoyaltySlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

/**
 * Puts the restaurant config and social media that getStaticProps already
 * carries into redux, so the browser no longer requests `/restaurant/<id>` or
 * `/restaurant/social-media/<id>` at all. #4204
 *
 * `/restaurant/<id>` returns the main restaurant AND every partner fully
 * populated, so one page's props cover every restaurant this storefront can
 * show — including whichever one the guest has switched to.
 */

/**
 * The restaurant the guest is actually looking at, taken out of the props.
 *
 * Core blanks each partner's own `partnerRestaurants` to avoid recursing, so a
 * partner is rebuilt with the list it needs to switch back: the main
 * restaurant plus its siblings. Everything else is already there — a partner
 * entry even carries the combined franchise list, exactly as the request did.
 */
export const resolveRestaurantFromProps = (
  propsRestaurant: Restaurant | undefined,
  wantedId: number | undefined
): Restaurant | undefined => {
  if (!propsRestaurant) {
    return undefined;
  }

  if (!wantedId || wantedId === propsRestaurant.id) {
    return propsRestaurant;
  }

  const partners = propsRestaurant.partnerRestaurants ?? [];
  const wanted = partners.find((partner) => partner.id === wantedId);

  if (!wanted) {
    // Not a restaurant this storefront serves — a franchise selection left
    // over from a previous deployment of this domain. Fall back to the
    // build-time restaurant, which is what the request's 4xx retry did.
    return propsRestaurant;
  }

  return {
    ...wanted,
    partnerRestaurants: [
      { ...propsRestaurant, partnerRestaurants: [] } as Restaurant,
      ...partners.filter((partner) => partner.id !== wanted.id),
    ],
  };
};

/** The browser did this filtering after the request; the props are unfiltered. */
const webImagesOnly = (restaurant: Restaurant): Restaurant => ({
  ...restaurant,
  restaurantImages: restaurant.restaurantImages?.filter(
    (image) => !image.source || image.source === RESTAURANT_IMAGE_SOURCE.WEB
  ),
});

const useRestaurantHydration = (props: {
  restaurantInfo?: Restaurant;
  socialMediaInfo?: Record<string, unknown>;
}) => {
  const dispatch = useAppDispatch();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );

  const propsRestaurant = props.restaurantInfo;

  // Same order of preference the request used to pick what to ask for.
  const wantedId =
    selectedFranchise?.restaurantId ??
    restaurantInfo?.id ??
    Number(envConfig.restaurantId);

  const restaurant = useMemo(
    () => resolveRestaurantFromProps(propsRestaurant, wantedId),
    [propsRestaurant, wantedId]
  );

  /**
   * Dispatched DURING RENDER, not from an effect.
   *
   * An effect meant every page went `restaurantInfo: null` -> render -> effect
   * -> dispatch -> re-render, and that extra pass broke things that had been
   * racing the old ordering: the self-service screen detached the button a test
   * was clicking, and an offline-mode screenshot caught the page mid-settle.
   * The browser request this replaced landed LATER, so nothing had ever seen a
   * null-then-populated flip this early.
   *
   * This component renders before its siblings, so a dispatch here lands in the
   * store before any of them render, and they read real config on their FIRST
   * pass. One render, no flip, and strictly earlier than the request it
   * replaced — so nothing that worked before can regress on ordering.
   *
   * It stays inside PersistGate: redux-persist REPLACES state when it
   * rehydrates, so dispatching above the gate would be silently overwritten by
   * whatever the guest had stored.
   *
   * useMemo is the render-phase hook with a dependency list; the work is a
   * dispatch rather than a computed value, and a repeat dispatch of the same
   * restaurant is idempotent, so a double invoke under StrictMode is harmless.
   */
  /**
   * Keyed on the restaurant AND its menuVersion, not the id alone.
   *
   * The id never changes for a storefront, so keying on it hydrated ONCE per
   * session and every later navigation returned early -- discarding a newer
   * authoritative payload while the reducer's own comment says the props are
   * authoritative. `restaurantInfo` is redux-persisted, so a returning guest
   * kept whatever menuVersion they had stored no matter how many pages they
   * opened.
   *
   * Nothing corrected that except the socket resync in BackgroundRequests,
   * which asks core on every connect -- 66,454 of those in eg in 24h on
   * 2026-10-11, almost all of them repair work for this guard. Observed on
   * ratio: the page served menuVersion 81, the database held 81, and the tab
   * asked core with 79.
   *
   * menuVersion moves on every menu change, which is exactly when a stale
   * payload starts to matter: the version a tab holds is what checkout sends,
   * and a stale one is rejected as ReqItemsContainsOutDatedItems.
   *
   * Still a ref rather than a plain dependency, so a re-render carrying the
   * same version does not re-dispatch -- `setAllRestaurantDetails` only
   * reassigns `selectedFranchise` when the franchise genuinely differs, and
   * SelfServiceScreen keys a cart reset off that identity.
   */
  const dispatchedRestaurant = useRef<string | undefined>(undefined);

  useMemo(() => {
    if (!restaurant) {
      return;
    }

    const key = `${restaurant.id}:${restaurant.menuVersion ?? ""}`;

    if (dispatchedRestaurant.current === key) {
      return;
    }

    dispatchedRestaurant.current = key;
    dispatch(hydrateRestaurantFromProps(webImagesOnly(restaurant)));
    dispatch(
      setHasRoundingMethodAndLevel(
        !!restaurant.currencyRoundingMethod &&
          !!restaurant.currencyRoundingLevel
      )
    );
  }, [restaurant?.id, restaurant?.menuVersion]);

  /**
   * Guarded by a ref, not by the dependency list.
   *
   * React 18 may run a render phase MORE THAN ONCE and throw the result away,
   * and useMemo is a hint, not a cache -- so a dependency list does not bound
   * how often this body runs. Dispatching from it therefore dispatched per
   * RENDER, and `setSocialMedia` assigns a fresh object every time, so the
   * state always looked changed, which re-rendered, which dispatched again.
   *
   * Measured on saporidicarlo 2026-09-08: config/setSocialMedia reached 1,419
   * actions and a /cart navigation took 5-7s with no network (82ms) and no long
   * tasks (66ms) -- the whole time was this loop. React 17 never re-ran a
   * render phase, which is why the old build was unaffected.
   *
   * The ref makes it fire once per actual VALUE. Serialised because the props
   * object is a new identity on every navigation while its content is almost
   * always the same `{}`.
   */
  const dispatchedSocialMedia = useRef<string | undefined>(undefined);

  useMemo(() => {
    if (!props.socialMediaInfo) {
      return;
    }

    const key = JSON.stringify(props.socialMediaInfo);

    if (dispatchedSocialMedia.current === key) {
      return;
    }

    dispatchedSocialMedia.current = key;
    dispatch(setSocialMedia(props.socialMediaInfo));
  }, [props.socialMediaInfo]);
};

export default useRestaurantHydration;
